import React, { useEffect, useState } from 'react';

const CIRCLE_R = 45;
const CIRCLE_R_SMALL = 28;
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_R;
const CIRCLE_CIRCUMFERENCE_SMALL = 2 * Math.PI * CIRCLE_R_SMALL;

export { CIRCLE_R, CIRCLE_CIRCUMFERENCE };

/** Single score gauge: circle filled by score/10, number in center. */
export default function ScoreGauge({
  score,
  label,
  size = 'normal',
  colorFn,
  ariaLabel,
  gaugeClass = '',
  breakdown,
  showTooltip,
  onTooltipToggle,
}) {
  const [displayScore, setDisplayScore] = useState(0);
  const isSmall = size === 'small';
  const r = isSmall ? CIRCLE_R_SMALL : CIRCLE_R;
  const circumference = isSmall ? CIRCLE_CIRCUMFERENCE_SMALL : CIRCLE_CIRCUMFERENCE;
  useEffect(() => {
    const t = requestAnimationFrame(() => setDisplayScore(score));
    return () => cancelAnimationFrame(t);
  }, [score]);
  const offset = circumference * (1 - displayScore / 10);
  const hasTooltip = breakdown && typeof onTooltipToggle === 'function';
  const tooltipLines = breakdown
    ? [
        (breakdown.education != null && breakdown.ba_plus_pct > 0) &&
          `Education: ${breakdown.education}/10 (${breakdown.ba_plus_pct}% college-educated)`,
        (breakdown.income != null && breakdown.median_income > 0) &&
          `Income: ${breakdown.income}/10 ($${breakdown.median_income.toLocaleString()} median)`,
        (breakdown.amenity != null && breakdown.amenity_count > 0) &&
          `Amenities: ${breakdown.amenity}/10 (${breakdown.amenity_count} establishments)`,
      ].filter(Boolean)
    : [];
  const wrapperClass = [
    'search-page__safety-gauge',
    gaugeClass,
    isSmall ? 'search-page__safety-gauge--small' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const content = (
    <>
      <div className="search-page__safety-gauge__circle-wrap">
        <svg viewBox="0 0 100 100" className="search-page__safety-gauge__ring" aria-hidden="true">
          <circle cx="50" cy="50" r={r} fill="none" className="search-page__safety-gauge__track" strokeWidth={isSmall ? 5 : 8} />
          <circle
            cx="50"
            cy="50"
            r={r}
            fill="none"
            className="search-page__safety-gauge__fill"
            strokeWidth={isSmall ? 5 : 8}
            strokeLinecap="round"
            stroke={colorFn(score)}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform="rotate(-90 50 50)"
          />
        </svg>
        <span className="search-page__safety-gauge__number">{score}</span>
      </div>
      {label && <span className="search-page__safety-gauge__label">{label}</span>}
      {hasTooltip && showTooltip && tooltipLines.length > 0 && (
        <div className="search-page__location-rating__tooltip search-page__livability-gauge__tooltip" role="tooltip">
          {tooltipLines.map((line, i) => <div key={i}>{line}</div>)}
        </div>
      )}
    </>
  );
  if (hasTooltip) {
    return (
      <div
        className={wrapperClass}
        aria-label={ariaLabel ?? `Livability ${score} out of 10`}
        onPointerEnter={(e) => { if (e.pointerType === 'mouse') onTooltipToggle('livability'); }}
        onPointerLeave={(e) => { if (e.pointerType === 'mouse') onTooltipToggle(null); }}
        onClick={() => onTooltipToggle(showTooltip ? null : 'livability')}
        style={{ position: 'relative', cursor: 'pointer' }}
      >
        {content}
      </div>
    );
  }
  return (
    <div className={wrapperClass} aria-label={ariaLabel}>
      {content}
    </div>
  );
}
